/**
 * Provides an interface for selecting the layers and services of a map to be displayed.
 * The interface includes a checked tree control which provides an intuitive way for users to control the visibility of the various services/layers. This tree control shall handle propagation in the following way:
 * * The "checking" of a child node shall propagate "checks" to all parent nodes between the child and root, but this check state shall *not* propagate to siblings of nodes at any level.
 * * The "unchecking" of a child node shall *not* propagate to parent nodes.
 * * The "checking" of a parent node shall *not* modify the check state of child nodes.
 * * The nodes that are checked represent the services, layers, and individual models to display.
 *
 * ## Events
 * Name | Arguments | Description
 * --- | --- | ---
 * model-check | The model, the layer containing the model, and an options hash. | Fires when the node associated with the given model is checked *or* when the node is checked and a parent node becomes checked.
 * model-uncheck | The model, the layer containing the model, and an options hash. | Fires when the node associated with the given model is checked *or* when the node is checked and a parent node becomes unchecked.
 * model-select | The model, the layer containing the model, and an options hash. | Fires when the node associated with the given model is selected.
 * layer-check | The layer and an options hash. | Fires when the node associated with the given layer is checked *or* when the node is checked and a parent node becomes checked.
 * layer-uncheck | The layer and an options hash. | Fires when the node associated with the given layer is checked *or* when the node is checked and a parent node becomes unchecked.
 * layer-select | The layer and an options hash. | Fires when the node associated with the given layer is selected.
 * service-check | The service and an options hash. | Fires when the node associated with the given service is checked *or* when the node is checked and a parent node becomes checked.
 * service-uncheck | The service and an options hash. | Fires when the node associated with the given service is checked *or* when the node is checked and a parent node becomes unchecked.
 * service-select | The service and an options hash. | Fires when the node associated with the given service is selected.
 * active-layer-changed | The new active layer. | Fires when the active layer is changed.
 *
 * *Note*: For the check, uncheck, and select events, the "options hash" refers to an object with two boolean properties, `includeChildren` and `inherited`. The `includeChildren` property is `true` when the original event is specified to propagate down to a node's children. The `inherit` property is `true` for events that fire as part of such a propagation. That is, for the original event, the `inherited` property will be `false`.
 * @module app/views/map/MapLayerView
 */
import MapDetailView from "app/views/map/MapDetailView";
import _ from "underscore";
import Field from "core/models/Field";
import FormInputView from "core/views/FormInputView";
import $ from "jquery";
import ErrorUtils from "app/utils/ErrorUtils";
import ServiceSelector from "app/views/map/ServiceSelector";
import TypeaheadView from "app/views/TypeaheadView";
import "jstree-bootstrap-theme/dist/themes/default/style.min.css";
import "jstree-bootstrap-theme/dist/themes/proton/style.min.css";
import "../../../../styles/specific/jstree.css";
import ViewHelper from "app/views/map/helpers/ViewHelper";
import cms from "core/context-menu-service";
import isMobile from "core/is-mobile";
import messenger from "app/Messenger";
import modernizr from "modernizr";
import app from "app/App";
import mapContext from "app/views/map/MapContext";
import _remove from "lodash.remove";

var textStrokeSupport = false;

export default MapDetailView.extend({
  className: "layer-view",

  /**
   * Initializes the view.
   * @param  {object} options The options hash.
   * @param {mixed} options.context The map context under which this instance is running.
   */
  initialize: function (options) {
    MapDetailView.prototype.initialize.apply(this, arguments);

    this.options = _.defaults({}, options, {
      //what do we need?
    });

    this._ignoreCheck = false;
    this._activeLayer = null;
    this._rootNodes = [];

    this._onServiceAdded = _.bind(function (service, index) {
      this._tree.set_type(this._nodeOf(service), "serviceLayer");

      return this._addServiceLayer(service, index + 1); //adding 1 here to account for working layer
    }, this);

    this._onServiceRemoved = _.bind(function (service) {
      this.removeServiceLayer(service);
      this.refreshActiveList();
      this.refreshServiceList();
      this.refreshControls();
    }, this);

    this._onLayerAdded = _.bind(function (service, parentLayer, layer, index) {
      var parentNode = this._nodeOf(parentLayer || service);
      var node = this._nodeOf(layer);

      if (!node || parentNode.id != node.parent) {
        //need to make sure it doesn't get added twice
        this._addLayerToTree(layer, parentNode, index);
        this._tree.open_node(parentNode);
      } else {
        this._tree.set_type(node, "Layer");
      }

      this.refreshActiveList();
    }, this);

    this._onLayerRemoved = _.bind(function (service, parentLayer, layer) {
      this.removeLayer(layer);
      this.refreshActiveList();
      this.refreshServiceList();
      this.refreshControls();
    }, this);

    var idx = 1;

    this._onModelAdded = _.bind(function (mapObject) {
      this.addModel(
        this.getConfiguration().workingLayer,
        mapObject,
        "Shape " + idx++,
        true,
        {
          li_attr: {
            class: "unsaved",
          },
        }
      );
    }, this);

    this._onModelRemoved = _.bind(function (mapObject) {
      this.removeModel(mapObject);
      this.refreshControls();
    }, this);

    this._refreshControls = _.bind(this.refreshControls, this);

    textStrokeSupport =
      modernizr.testAllProps("-webkit-text-stroke-color") &&
      modernizr.testAllProps("-webkit-text-stroke-width");
  },

  template: _.template('<div class="js-tree"></div>'),

  _onUnCheckNode: function (evt, args) {
    this._notify(args.node, "uncheck", {
      includeChildren: true,
    });
  },

  _notify: function (node, eventName, options) {
    options = _.defaults({}, options, {
      includeChildren: false,
      inherited: false,
    });

    if (node.data) {
      if (node.data.model) {
        this.trigger(
          "model-" + eventName,
          node.data.model,
          node.data.layer,
          options
        );
      } else if (node.data.layer) {
        this.trigger("layer-" + eventName, node.data.layer, options);
      } else if (node.data.service) {
        this.trigger("service-" + eventName, node.data.service, options);
      }
    }

    if (options.includeChildren) {
      _.each(
        node.children,
        _.bind(function (childId) {
          var child = this._tree.get_node(childId);

          if (this._tree.is_checked(child)) {
            this._notify(
              child,
              eventName,
              _.extend(options, {
                includeChildren: true,
                inherited: true,
              })
            );
          }
        }, this)
      );
    }
  },

  _onCheckNode: function (evt, args) {
    if (args.node) {
      if (!this._ignoreCheck) {
        var subject = args.node;
        var parent = this._tree.get_parent(args.node);

        if (parent !== "#" && !this._tree.is_checked(parent)) {
          this._tree.check_node(parent);
          subject = parent; //this will cause the current node to notify, since _notify is recursive
        }

        this._notify(subject, "check", {
          includeChildren: true,
        });
      }
    }
  },

  _onSelectNode: function (evt, args) {
    this._notify(args.node, "select");
  },

  _onDoubleClick: function (evt) {
    var originalTarget = evt.target;

    //If the original target is jstree-checkbox, don't perform doubleClick action(Do not 'zoom-to-layer')
    if (
      originalTarget &&
      _.contains(originalTarget.classList, "jstree-checkbox")
    ) {
      return;
    }
    var target = this._tree.get_node(originalTarget);
    var currentTarget = this._tree.get_node(evt.currentTarget);

    if (target == currentTarget) {
      this._notify(target, "doubleclick");
    }
  },

  _updateEsriLegend: function (node) {
    var childNode = this._tree.get_node(node);
    var $childNode = this._tree.get_node(childNode, true);
    var layer = childNode.data.layer;
    var mapLayer = layer.getMapLayer();
    var isUnique =
      mapLayer &&
      mapLayer.renderer &&
      _.contains(
        ["uniqueValue", "classBreaks"],
        mapLayer.renderer.toJson().type
      );

    if (childNode._legend) {
      if (childNode._legendStarted) {
        childNode._legend.destroy();
        childNode._legendStarted = false;
        this._createNodeLegend(childNode);
      }

      if (isUnique) {
        $childNode.append("<br/>");
        $childNode
          .find(".jstree-themeicon")
          .appendTo($childNode)
          .append(childNode._legend.domNode)
          .addClass("unique-value-legend")
          .removeClass("layer-node");
      } else {
        $childNode
          .find(".jstree-themeicon")
          .append(childNode._legend.domNode)
          .addClass("standard-legend")
          .removeClass("layer-node");
      }

      childNode._legend.startup();

      var $legendMessage = $childNode.find(".esriLegendMsg");

      if (!mapLayer.visible && isUnique) {
        $legendMessage.html("Not Shown");
      }

      if ($legendMessage.html() == childNode._legend.NLS_noLegend) {
        $legendMessage.html("");
      }

      childNode._legendStarted = true;
    }
  },

  _onBeforeOpen: function (evt, args) {
    _.each(args.node.children, this._updateEsriLegend, this);
  },

  events: {
    "select_node.jstree .js-tree": "_onSelectNode",
    "check_node.jstree .js-tree": "_onCheckNode",
    "uncheck_node.jstree .js-tree": "_onUnCheckNode",
    "dblclick .js-tree .jstree-node": "_onDoubleClick",
    "before_open.jstree .js-tree": "_onBeforeOpen",
  },

  /**
   * Adds the given CSS class to the node.
   * @param {string|object} nodeId    The node to which the class shall be added.
   * @param {string} className A CSS class (or concatenated list of classes).
   */
  addNodeClass: function (nodeId, className) {
    this._tree.get_node(nodeId, true).addClass(className);
  },

  /**
   * Removes the given CSS class from the node.
   * @param {string|object} nodeId    The node from which the class shall be removed.
   * @param {string} className A CSS class (or concatenated list of classes).
   */
  removeNodeClass: function (nodeId, className) {
    this._tree.get_node(nodeId, true).removeClass(className);
  },

  addServiceLayer: function (serviceLayer, index) {
    var result = this._addServiceLayer(serviceLayer, index);

    this.refreshActiveList();

    return result;
  },

  /**
   * Adds a service to the tree.
   * @private
   * @param {MapServiceBase} serviceLayer The service to add.
   * @param {Integer} [index]        An index at which to add the service. If not specified, it will be added to the end of the list.
   * @return {mixed} The node that was added.
   */
  _addServiceLayer: function (serviceLayer, index) {
    var result = null;

    if (this._tree) {
      var tree = this._tree;
      var self = this;

      if (serviceLayer.service) {
        this.listenTo(serviceLayer.service, "change:name", function () {
          tree.set_text(this._nodeOf(serviceLayer), serviceLayer.name);
        });
      }

      result = tree.create_node(
        null,
        {
          icon: false,
          text: serviceLayer.name,
          type: "serviceLayer",
          data: {
            service: serviceLayer,
          },
        },
        index,
        function (node) {
          tree.set_type(node, "serviceLayer");
          //self._ignoreCheck = true;
          self._rootNodes.push(node);
          _.each(serviceLayer.getLayers(), function (layer) {
            self._addLayerToTree(layer, node);
          });
          self._ignoreCheck = false;

          if (serviceLayer.visible) {
            tree.check_node(node);
          } else {
            tree.uncheck_node(node);
          }
        }
      );

      var desktopContextMenu = [
        mapContext.getOperation("remove-service").withModel(serviceLayer),
        mapContext.getOperation("delete-service").withModel(serviceLayer),
        mapContext.getOperation("add-layer").withModel(serviceLayer),
        mapContext
          .getOperation("edit-service-security")
          .withModel(serviceLayer),
        mapContext.getOperation("paste-layer").withModel(serviceLayer),
        true,
        mapContext.getOperation("pan-to-item").withModel(serviceLayer),
        mapContext.getOperation("zoom-to-item").withModel(serviceLayer),
        true,
        mapContext.getOperation("service-select-all").withModel(serviceLayer),
        mapContext.getOperation("service-tabulate-all").withModel(serviceLayer),
        mapContext.getOperation("view-item-files").withModel(serviceLayer),
        mapContext.getOperation("download-item").withModel(serviceLayer),
      ];

      var mobileContextMenu = [
        ViewHelper.getCheckAllAction(tree, result),
        ViewHelper.getUncheckAllAction(tree, result),
      ];

      var contextMenu = mobileContextMenu;

      if (!isMobile) {
        contextMenu = desktopContextMenu
          .concat([true])
          .concat(mobileContextMenu);
      }

      /*
			_.each(this.options.context.plugins, function (plugin) {
				if (plugin.handlesService(serviceLayer)) {
					var operations = plugin.getServiceOperations();

					if (operations.length) {
						contextMenu.push([plugin.name].concat(_.map(operations, function (operation) {
							return operation.bind(app, this.options.context).withModel(serviceLayer);
						}, this)));
					}
				}
			}, this);
			*/

      cms.attachContextMenu("#" + result + " > a", contextMenu);

      this._nodeOf(serviceLayer, result);
      tree.set_type(this._nodeOf(serviceLayer, result), "serviceLayer");
    }

    return result;
  },

  /**
   * Adds a layer to the children of the specified parent object.
   * @param {MapLayerBase} layer  The layer to add.
   * @param {MapServiceBase|MapLayerBase} parent The parent to which to add the layer.
   * @param {Integer} index  An index at which to add the layer. If not specified, it will be added to the end of the list of siblings.
   */
  addLayer: function (layer, parent, index) {
    var result = this._addLayerToTree(
      layer,
      parent ? this._nodeOf(parent) : null,
      index
    );

    this.refreshActiveList();

    return result;
  },

  /**
   * Adds a model to the children of the specified object.
   * @param {MapLayerBase} layer   The layer under which to add the model.
   * @param {object} model   The model to add.
   * @param {string} name    The node name.
   * @param {Boolean} visible True if the object should be initially visible, otherwise false.
   * @param {object} options Any additional options for the node object.
   * @param {Integer} index   An index at which to add the model. If not specified, it will be added to the end of the list of siblings.
   */
  addModel: function (layer, model, name, visible, options, index) {
    var opts = _.extend(
      {
        icon: false,
        text: name,
        type: "model",
        data: {
          model: model,
          layer: layer,
        },
      },
      options
    );
    var tree = this._tree;
    var parent = this._nodeOf(layer);
    var self = this;

    return tree.create_node(parent, opts, index, function (node) {
      self._ignoreCheck = true;
      tree.set_type(node, "model");

      if (visible) {
        tree.check_node(node);
      } else {
        tree.uncheck_node(node);
      }

      self._nodeOf(model.getFeature(), node);
      self._ignoreCheck = false;
    });
  },

  /**
   * Reset the text for the node associated with a given model.
   * @param  {object} model The model associated with the node.
   * @param  {string} name  The new node text.
   */
  refreshModelName: function (model, name) {
    this._tree.rename_node(this._nodeOf(model.getFeature()), name);
  },

  /**
   * Reset the text for the node associated with a given layer.
   * @param  {object} layer The layer associated with the node.
   * @param  {string} name  The new node text. If not specified, the `name` property of the `layer` argument will be used.
   */
  refreshLayerName: function (layer, name) {
    this._tree.rename_node(this._nodeOf(layer), name || layer.name);
  },

  /**
   * Reset the text for the node associated with a given service.
   * @param  {object} serviceLayer The service associated with the node.
   * @param  {string} name         The new node text. If not specified, the `name` property of the `serviceLayer` argument will be used.
   */
  refreshServiceLayerName: function (serviceLayer, name) {
    this._tree.rename_node(
      this._nodeOf(serviceLayer),
      name || serviceLayer.name
    );
  },

  /**
   * Removes a model from the interface.
   * @param  {object} model The model to remove.
   */
  removeModel: function (model) {
    this._remove(model.getFeature());
  },

  _remove: function (model) {
    if (this._tree) {
      var node = this._nodeOf(model);

      if (node) {
        this._tree.delete_node(node);
      }
    }

    this._clearNode(model);
  },

  _createNodeStyle: function (node) {
    return $("<style></style>")
      .attr("id", node.id + "_style")
      .appendTo("head");
  },

  _updateNodeStyle: function (node, $nodeStyle, symbolFactory) {
    var fillColor = symbolFactory.get("fillColor").toString();
    var color = symbolFactory.get("color").toString();
    var style = symbolFactory.get("markerStyle");
    var lineWidth = symbolFactory.get("lineWidth");

    var squareHtmlFunction = function (node, color, fillColor, lineWidth) {
      if (textStrokeSupport) {
        return (
          "#" +
          node.id +
          this.class +
          "{" +
          "color: " +
          fillColor +
          ";" +
          "-webkit-text-stroke-color:" +
          color +
          ";" +
          "-webkit-text-stroke-width:" +
          lineWidth.toString() +
          "px" +
          ";" +
          "}"
        );
      } else {
        //use a shadow to recreate the border for the diamond legend icon
        return (
          "#" +
          node.id +
          this.class +
          "{" +
          "color: " +
          fillColor +
          ";" +
          "text-shadow: " +
          " -1px -1px 0 " +
          color +
          "," +
          " 1px -1px 0 " +
          color +
          "," +
          " -1px 1px 0 " +
          color +
          "," +
          " 1px 1px 0 " +
          color +
          ";" +
          "}"
        );
      }
    };

    var crossHtmlFunction = function (node, color, fillColor, lineWidth) {
      return "#" + node.id + this.class + "{" + "color: " + color + ";" + "}";
    };

    var styleByIconObject = {
      cross: {
        class: " .legend-cross",
        iconClass: "glyphicon glyphicon-plus legend-cross",
        htmlBuilder: crossHtmlFunction,
      },
      diamond: {
        class: " .legend-diamond",
        iconClass: "glyphicon glyphicon-stop legend-diamond",
        htmlBuilder: squareHtmlFunction,
      },
      x: {
        class: " .legend-cross",
        iconClass: "glyphicon glyphicon-remove legend-cross",
        htmlBuilder: crossHtmlFunction,
      },
      square: {
        class: " .legend-square",
        iconClass: "glyphicon glyphicon-stop legend-square",
        htmlBuilder: squareHtmlFunction,
      },
    };

    var defaultOptions = {
      class: " .layer-node",
      iconClass: "layer-node",
      htmlBuilder: function (node, color, fillColor, lineWidth) {
        return (
          "#" +
          node.id +
          this.class +
          "{" +
          "background-color: " +
          fillColor +
          ";" +
          "border-color: " +
          color +
          ";" +
          "}"
        );
      },
    };

    //If an icon object is provided, use those icon/style options. If not, style it like a circle.
    var opts = _.defaults(styleByIconObject[style] || {}, defaultOptions);
    var nodeHtml = opts.htmlBuilder(node, color, fillColor, lineWidth);

    $nodeStyle.html(nodeHtml);
    this._tree.set_icon(node, opts.iconClass);
  },

  _createNodeLegend: function (node) {
    var Legend = window.require("esri/dijit/Legend");
    var layer = node.data.layer;
    var type = node.type;
    var self = this;
    var mapLayer = layer.getMapLayer();
    var $nodeStyle;

    if (
      type != "workingLayer" &&
      layer.serviceLayer.service.get("external") &&
      !layer.hasChildren()
    ) {
      node._legend = new Legend(
        {
          map: mapContext.getMap(),
          autoUpdate: false,
          respectVisibility: false,
          layerInfos: [
            {
              defaultSymbol: true,
              hideLayers: _.pluck(layer.children, "id"),
              title: layer.name,
              layer: mapLayer,
            },
          ],
        },
        "#" + node.id
      );
    } else if (type == "workingLayer" || !layer.hasChildren()) {
      if (_.isFunction(layer.getSymbolFactory)) {
        $nodeStyle = this._createNodeStyle(node);
        var updateLegendStyle = function updateLegendStyle() {
          self._updateNodeStyle(node, $nodeStyle, layer.getSymbolFactory());
        };

        var symbolPropertiesToMonitor =
          "change:color change:fillColor change:markerStyle change:lineWidth";

        this.listenTo(
          layer.getSymbolFactory(),
          symbolPropertiesToMonitor,
          function () {
            updateLegendStyle();
          }
        );

        //Listen to the layer model. When a new symbolFactory is added to the layer model, reattach event listeners and updateLegendStyle
        this.listenTo(layer, "change:symbolFactory", function () {
          self.listenTo(
            layer.getSymbolFactory(),
            symbolPropertiesToMonitor,
            function () {
              updateLegendStyle();
            }
          );
          updateLegendStyle();
        });

        updateLegendStyle();
      }
    } else if (layer.hasChildren()) {
      $nodeStyle = this._createNodeStyle(node);

      $nodeStyle.html(
        "#" +
          node.id +
          " .layer-node" +
          "{" +
          "background-color: transparent;" +
          "border-color: transparent;" +
          "width: auto;" +
          "height: auto;" +
          "}"
      );
    }
  },

  _addLayerToTree: function (layer, defaultParent, index, workingLayer) {
    var tree = this._tree;
    var parentNode = defaultParent || null;
    var self = this;
    var type = "Layer";

    if (workingLayer) {
      type = "workingLayer";
    }

    if (layer.featureLayer) {
      this.listenTo(layer.featureLayer, "change:name", function () {
        tree.set_text(this._nodeOf(layer), layer.name);
      });
    }

    var result = tree.create_node(
      parentNode,
      {
        icon: "layer-node",
        text: layer.name,
        type: type,
        data: {
          layer: layer,
        },
      },
      index,
      function (node) {
        tree.set_type(node, type);

        if (!defaultParent) {
          self._rootNodes.push(node);
        }

        _.each(layer.children, function (childLayer) {
          self._addLayerToTree(childLayer, node);
        });

        if (layer.visible) {
          tree.check_node(node);
        } else {
          tree.uncheck_node(node);
        }

        self._createNodeLegend(node);

        self._nodeOf(layer, node);
      }
    );

    var contextMenu = [
      mapContext.getOperation("copy-layer").withModel(layer),
      mapContext.getOperation("edit-layer").withModel(layer),
      mapContext.getOperation("delete-layer").withModel(layer),
      //mapContext.getOperation('add-layer').withModel(layer),
      mapContext.getOperation("manage-layer-security").withModel(layer),
      mapContext.getOperation("layer-bulk-add").withModel(layer),
      true,
      mapContext.getOperation("pan-to-item").withModel(layer),
      mapContext.getOperation("zoom-to-item").withModel(layer),
      true,
      mapContext.getOperation("layer-select-all").withModel(layer),
      mapContext.getOperation("layer-tabulate-all").withModel(layer),
      mapContext.getOperation("layer-print-all").withModel(layer),
      mapContext.getOperation("download-item").withModel(layer),
      mapContext.getOperation("view-item-files").withModel(layer),
    ];

    if (isMobile) {
      contextMenu = [];
    }

    if (layer.hasChildren()) {
      if (!isMobile) {
        contextMenu.push(true);
      }

      contextMenu.push(ViewHelper.getCheckAllAction(tree, result));
      contextMenu.push(ViewHelper.getUncheckAllAction(tree, result));
    }

    /*
		_.each(this.options.context.plugins, function (plugin) {
			if (plugin.handlesLayer(layer)) {
				var operations = plugin.getLayerOperations();

				if (operations.length) {
					contextMenu.push([plugin.name].concat(_.map(operations, function (operation) {
						return operation.bind(app, this.options.context).withModel(layer);
					}, this)));
				}
			}
		}, this);
		*/

    cms.attachContextMenu("#" + result + " > a", contextMenu);

    return result;
  },

  /**
   * Removes a service from the interface. Any child layers will also be removed.
   * @param  {MapServiceBase} serviceLayer The service to remove.
   */
  removeServiceLayer: function (serviceLayer) {
    _remove(this._rootNodes, this._nodeOf(serviceLayer));
    this._remove(serviceLayer);
  },

  /**
   * Removes a layer from the interface.
   * @param  {MapLayerBase} layer The layer to remove.
   */
  removeLayer: function (layer) {
    this._remove(layer);
  },

  _nodeOf: function (item, node) {
    if (node) {
      item._node = node;
    } else {
      if (item._node) {
        return this._tree.get_node(item._node);
      } else {
        return this._tree.get_node(item.id);
      }
    }
  },

  _clearNode: function (item) {
    delete item._node;
  },

  /**
   * Gets the tree node that is the parent of the given item.
   * @param  {object} item The item associated with the node.
   * @return {object} The parent node.
   */
  parentOf: function (item) {
    var node = this._nodeOf(item);

    return this._tree.get_node(node.parent);
  },

  /**
   * Gets the index of the given item amongst its siblings in the tree.
   * @param  {object} item The item associated with the node.
   * @return {Integer} The index of the item in its list of siblings.
   */
  indexOf: function (item) {
    var node = this._nodeOf(item);
    var parent = this.parentOf(item);

    return parent.children.indexOf(node.id);
  },

  _buildLayerNames: function () {
    var layerNames = [];
    var self = this;

    function createNames(node, parentNode, prefix) {
      var childPrefix;
      var children = _.map(node.children, function (child) {
        return self._tree.get_node(child);
      });
      var skip = false;
      var name;

      if (node.data.service) {
        childPrefix = node.data.service.name + " -> ";
      } else if (node.data.layer) {
        if (!parentNode || parentNode.data.service) {
          //if we are at a root layer
          childPrefix = "-";
        } else {
          childPrefix = prefix + "-";
        }

        name = prefix + node.data.layer.name;

        if (!node.data.layer.isEditable()) {
          skip = true;
        }
      } else {
        //skip if we don't have the right data
        skip = true;
      }

      if (!skip) {
        if (node.data.layer) {
          layerNames.push({
            value: node.id,
            text: name,
            type: "Layer",
          });
        }

        _.each(children, function (childNode) {
          if (childNode) {
            createNames(childNode, node, childPrefix);
          }
        });
      }
    }

    _.each(self._rootNodes, function (rootNode) {
      var node = self._tree.get_node(rootNode.id);

      if (node) {
        createNames(node, null, "");
      }
    });

    return layerNames;
  },

  /**
   * Refreshes the selection control for active layers.
   */
  refreshActiveList: function () {
    if (this.layerSelect) {
      this.layerSelect.remove();
    }

    this.layerSelect = this._createActiveInput();
    this.$el.prepend(this.layerSelect.$el);
  },

  /**
   * Add Service list
   */
  refreshServiceList: function () {
    if (this.serviceList) {
      this.serviceList.remove();
    }

    this.serviceList = this._createServiceInput();

    var $searchView = this.$(".form-input-view-search");

    if ($searchView.length) {
      $searchView.before(this.serviceList.$el);
    } else {
      this.$(".js-tree").before(this.serviceList.$el);
    }
  },

  /**
   * Refreshes the controls available on the toolbar based on current selections.
   */
  refreshControls: function () {
    var layout = mapContext.getConfiguration(),
      service = mapContext.getSelectedService();

    this.clearControls();
    if (isMobile) {
      this.addControl(
        this.createIconButton(
          mapContext.getOperation("save-layout").withModel(layout)
        )
      );
      this.addControl(
        this.createIconButton(
          mapContext.getOperation("create-layout").withModel(layout)
        )
      );
      this.addControl(
        this.createIconButton(
          mapContext.getOperation("add-service").withModel(layout)
        )
      );
      this.addControl(
        this.createIconButton(
          mapContext.getOperation("remove-service").withModel(service)
        )
      );
    } else {
      this.addControl(
        this.createIconButton(
          mapContext.getOperation("save-layout").withModel(layout)
        )
      );
      this.addControl(
        this.createIconButton(
          mapContext.getOperation("create-layout").withModel(layout)
        )
      );
      this.addControl(
        this.createIconButton(
          mapContext.getOperation("edit-layout").withModel(layout)
        )
      );
      this.addControl(
        this.createIconButton(
          mapContext.getOperation("edit-security").withModel(layout)
        )
      );
      this.addControl(
        this.createIconButton(
          mapContext.getOperation("delete-layout").withModel(layout)
        )
      );
      this.addControl(
        this.createIconButton(
          mapContext.getOperation("add-service").withModel(layout)
        )
      );
      this.addControl(
        this.createIconButton(
          mapContext.getOperation("add-layer").withModel(service)
        )
      );
      this.addControl(
        this.createIconButton(
          mapContext.getOperation("remove-service").withModel(service)
        )
      );
    }
  },

  render: function () {
    this.$el.html(this.template());

    this.$(".js-tree").jstree({
      types: {
        "#": {
          valid_children: ["serviceLayer", "default", "workingLayer"],
        },
        default: {
          valid_children: ["default", "Layer"],
        },
        serviceLayer: {
          valid_children: ["Layer", "default"],
        },
        workingLayer: {
          valid_children: ["default", "model"],
        },
        Layer: {
          valid_children: ["Layer", "model", "default"],
        },
        model: {
          valid_children: [],
        },
      },
      core: {
        check_callback: function (operation, node, nodeParent) {
          // operation can be 'create_node', 'rename_node', 'delete_node', 'move_node' or 'copy_node'
          // in case of 'rename_node' node_position is filled with the new node name

          if (operation === "move_node") {
            //check if the layer being dragged is external and if it is return false
            var oNodeParent = $.jstree
              .reference(".js-tree")
              .get_node(node.parent);
            var service;

            if (oNodeParent.data && oNodeParent.data.service) {
              service = oNodeParent.data.service;
            }

            if (oNodeParent.type === "#") {
              //don't move working layers .node.type == 'workingLayer
              if (node.type === "workingLayer") {
                return false;
              }

              return true;
            } else if (node.type === "model") {
              return false;
            } else if (service && service.service.get("external")) {
              return false;
            } else if (node.type == "Layer" && nodeParent.type == "Layer") {
              //Disable sublayers. Return false if moving to another layer.
              return false;
            }
          }

          return true; //allow all other operations
        },
        themes: {
          name: "proton",
          responsive: true,
        },
      },
      checkbox: {
        tie_selection: false,
        whole_node: false,
        three_state: false,
        cascade: "",
      },
      dnd: {
        //'drop_target': false,
        //'drag_target': false,
        inside_pos: 0,
        open_timeout: 0,
        large_drop_target: true,
        large_drag_target: true,
        check_while_dragging: true,
        drag_selection: false,
      },
      search: {
        fuzzy: false,
        show_only_matches: true,
        show_only_matches_children: true,
      },
      plugins: ["checkbox", "dnd", "crrm", "types", "search"],
    });

    //apparently the events hash won't work for a nested jstree?
    this.$(".js-tree").on(
      "select_node.jstree",
      _.bind(this._onSelectNode, this)
    );
    this.$(".js-tree").on("check_node.jstree", _.bind(this._onCheckNode, this));
    this.$(".js-tree").on(
      "uncheck_node.jstree",
      _.bind(this._onUnCheckNode, this)
    );
    this.$(".js-tree").on("move_node.jstree", _.bind(this._onMoveNode, this));
    this.$(".js-tree").on(
      "before_open.jstree",
      _.bind(this._onBeforeOpen, this)
    );
    //this.$('.js-tree').on('check_move.jstree', _.bind(this._checkMoveNode, this));

    this._tree = $.jstree.reference(this.$(".js-tree"));

    var inputView = new FormInputView({
      field: new Field({
        id: "search",
        name: "search",
        label: "Search Layers/Services",
        editor: "text",
      }),
      model: {
        search: "",
      },
    });

    inputView.render();
    inputView.$el.insertBefore(this.$(".js-tree"));
    inputView.on(
      "change",
      function (value) {
        if (value) {
          this._tree.search(value);
        } else {
          this._tree.clear_search();
        }
      },
      this
    );

    this.setConfiguration(mapContext.getConfiguration());
  },

  /**
   * Gets the configuration associated with this layer view.
   * @return {Layout} The configuration.
   */
  getConfiguration: function () {
    return this._config;
  },

  /**
   * triggers when a node is moved
   * @return {Layout} The configuration.
   */
  _onMoveNode: function (e, data) {
    var options;

    if (data.parent === "#" && data.old_parent === "#") {
      //we are moving services

      options = {
        newIndex: data.position,
        oldIndex: data.old_position,
      };

      mapContext
        .getOperation("move-service")
        .action(data.node.data.service, options)
        .fail(
          ErrorUtils.errorHandler({
            activity: "moving the service",
          })
        );
    } else {
      // we are moving a layer
      options = {
        oldParent: this._tree.get_node(data.old_parent),
        newParent: this._tree.get_node(data.parent),
        newIndex: data.position,
        oldIndex: data.old_position,
      };

      mapContext
        .getOperation("move-layer")
        .action(data.node.data.layer, options)
        .fail(
          ErrorUtils.errorHandler({
            activity: "moving the layer",
          })
        );
    }
  },

  /**
   * Sets the configuration associated with this layer view.
   * @param {Layout} config The configuration.
   */
  setConfiguration: function (config) {
    if (this._config) {
      this._remove(this._config.workingLayer);
      _.each(
        this._config.serviceLayers,
        function (serviceLayer) {
          this.removeServiceLayer(serviceLayer);
        },
        this
      );

      this.stopListening(this._config);
      this.stopListening(this._config.workingLayer);
    }

    this._config = config;

    if (this._config) {
      this._addLayerToTree(this._config.workingLayer, null, void 0, true); // add the workspace layer at the service level
      _.each(this._config.serviceLayers, this._onServiceAdded);

      this.listenTo(this._config, "service-added", this._onServiceAdded);
      this.listenTo(this._config, "service-removed", this._onServiceRemoved);
      this.listenTo(this._config, "layer-added", this._onLayerAdded);
      this.listenTo(this._config, "layer-removed", this._onLayerRemoved);
      this.listenTo(this._config, "activate-service", this._refreshControls);
      this.listenTo(this._config, "activate-layer", this._refreshControls);
      this.listenTo(
        this._config.workingLayer,
        "model-added",
        this._onModelAdded
      );
      this.listenTo(
        this._config.workingLayer,
        "model-removed",
        this._onModelRemoved
      );

      this.refreshActiveList();
      this.refreshServiceList();
      this.refreshControls();
    }
  },

  _createActiveField: function () {
    var self = this;
    var getValue = function (model) {
      var result = null;
      var activeLayer = model.getDrawingLayer();

      if (activeLayer) {
        var node = self._nodeOf(activeLayer);

        if (node) {
          result = node.id; //we're using the node id as a unique identifier for each layer
        }
      }

      return result;
    };
    var setValue = function (model, value) {
      var node = self._tree.get_node(value);

      if (node && node.data && node.data.layer) {
        model.setDrawingLayer(node.data.layer);
      }
    };

    return new Field(
      {
        id: "layer",
        name: "layer",
        label: "Active Layer",
        values: this._buildLayerNames(),
      },
      {
        setter: setValue,
        getter: getValue,
      }
    );
  },

  _createActiveInput: function () {
    var inputView = new FormInputView({
      field: this._createActiveField(),
      model: this._config,
    });

    inputView.render();

    return inputView;
  },

  _createServiceInput: function () {
    var self = this;
    var services = self.getConfiguration().serviceModels;

    var typeahead = new TypeaheadView({
      placeholder: "Add a Service",
      source: services.models,
      displayText: function (item) {
        return item.get("name");
      },
    });

    typeahead.render();

    services.fetch().done(function () {
      typeahead.setSource(services.models);
    });

    typeahead.on(
      "select",
      function (selectedServiceModel) {
        var service = ServiceSelector.createService(selectedServiceModel);

        messenger.info("Loading service");
        service
          .load()
          .done(function () {
            self
              .getConfiguration()
              .addService(service)
              .done(function () {
                messenger.success("Service added successfully.");
              })
              .fail(
                ErrorUtils.errorHandler({
                  activity: "adding service",
                })
              );
          })
          .fail(
            ErrorUtils.errorHandler({
              activity: "loading service",
            })
          );

        // trying to make sure our local store is as up-to-date as possible
        services.fetch().done(function () {
          typeahead.setSource(services.models);
        });
      },
      this
    );

    return typeahead;
  },
});



//////////////////
// WEBPACK FOOTER
// ./public/js/app/views/map/MapLayerView.js
// module id = 943
// module chunks = 1